import {
	datePeriodToRange,
	getDateRefinementLabel,
	isValidTimeZone,
	rangeToDatePeriod,
} from './algolia-filter-date';
import { getRouting } from './algolia-filter-routing';

const queue = window.aceeeAlgoliaFilterQueue;
const unavailableMessage = 'Search is temporarily unavailable. Please try again later.';
const maxFacetValues = 1000;
const searchDebounceDelay = 200;
const scheduledRoots = new WeakSet();
const initializedRoots = new WeakSet();

export const createSearchQueryHook = () => {
	let timerId;

	return (query, search) => {
		window.clearTimeout(timerId);

		if (query === '') {
			search(query);
			return;
		}

		timerId = window.setTimeout(() => search(query), searchDebounceDelay);
	};
};

const getMount = (root, name) => root.querySelector(`[data-algolia-mount="${name}"]`);
const getMounts = (root, name) => [
	...root.querySelectorAll(`[data-algolia-mount="${name}"]`),
];

const getExternalSearchHost = (root) => (
	[...document.querySelectorAll('[data-algolia-search-host]')]
		.find((host) => host.dataset.algoliaSearchHost === root.id) || null
);

const getSearchMount = (root) => (
	getMount(root, 'search')
	|| getExternalSearchHost(root)?.querySelector('[data-algolia-mount="search"]')
	|| null
);

const moveSearchToExternalHost = (root) => {
	const host = getExternalSearchHost(root);
	const mount = getMount(root, 'search');
	const search = mount?.closest('.algolia-filter__search');

	if (!host || !search) {
		return;
	}

	host.replaceChildren(search);
};

const setStatus = (root, message, mode = '') => {
	const status = getMount(root, 'status');
	if (!status) {
		return;
	}

	status.textContent = message;
	status.classList.toggle('algolia-filter__status--error', mode === 'error');
	status.classList.toggle('algolia-filter__status--announcement', mode === 'announcement');
};

const setDateError = (root, message) => {
	const error = root.querySelector('[data-algolia-date="error"]');
	if (error) {
		error.textContent = message;
	}
};

const associateSearchInput = (root, instanceId, query = '') => {
	const input = getSearchMount(root)?.querySelector('input[type="search"]');
	if (input) {
		input.id = `${instanceId}-search-input`;
		if (input.value !== query) {
			input.value = query;
		}
	}
};

const getPostTypeFilter = (postTypes) => postTypes
	.map((postType) => `post_type:"${postType.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`)
	.join(' OR ');

const getSafeUrl = (value) => {
	if (typeof value !== 'string' || !value.trim()) {
		return '';
	}

	try {
		const url = new URL(value, window.location.origin);
		return ['http:', 'https:'].includes(url.protocol) ? value : '';
	} catch (error) {
		return '';
	}
};

const getSafeSrcset = (value) => {
	if (typeof value !== 'string' || !value.trim()) {
		return '';
	}

	const candidates = value.split(',').map((candidate) => candidate.trim()).filter(Boolean);
	const safeCandidates = candidates.filter((candidate) => {
		const [url, descriptor = ''] = candidate.split(/\s+/, 2);
		return getSafeUrl(url) && (!descriptor || /^\d+(?:\.\d+)?[wx]$/.test(descriptor));
	});

	return safeCandidates.length === candidates.length ? safeCandidates.join(', ') : '';
};

const getScalar = (value) => (
	typeof value === 'string' || typeof value === 'number' ? String(value) : ''
);

const getTagValues = (tags) => (
	Array.isArray(tags)
		? tags.map((tag) => {
			const text = getScalar(tag?.text);
			const taxonomy = getScalar(tag?.taxonomy);
			const link = getSafeUrl(tag?.link);
			return text ? {
				text,
				taxonomy: /^[a-z0-9_-]+$/.test(taxonomy) ? taxonomy : '',
				link,
			} : null;
		}).filter(Boolean)
		: []
);

const getMetaValues = (rows) => {
	if (!Array.isArray(rows)) {
		return [];
	}

	return rows.flatMap((row) => {
		if (!row || typeof row !== 'object' || Array.isArray(row)) {
			return [];
		}
		return Object.entries(row).flatMap(([key, value]) => {
			const text = getScalar(value);
			return text && /^[a-z0-9_-]+$/.test(key) ? [{ key, text }] : [];
		});
	});
};

const snippetMatchScores = {
	none: 0,
	partial: 1,
	full: 2,
};

export const getSnippetAttribute = (hit) => {
	const excerpt = hit._snippetResult?.excerpt;
	const content = hit._snippetResult?.content;

	if (!excerpt) {
		return content ? 'content' : '';
	}

	if (
		content
		&& (snippetMatchScores[content.matchLevel] ?? 0)
			> (snippetMatchScores[excerpt.matchLevel] ?? 0)
	) {
		return 'content';
	}

	return 'excerpt';
};

export const getHitTemplate = (hit, { html, components }) => {
	const permalink = getSafeUrl(hit.permalink);
	const fallbackTitle = getScalar(hit.title);
	const title = hit._highlightResult?.title
		? components.Highlight({ hit, attribute: 'title' })
		: fallbackTitle;
	const hasTitle = Boolean(hit._highlightResult?.title || fallbackTitle);
	const snippetAttribute = getSnippetAttribute(hit);
	const abstract = snippetAttribute
		? components.Snippet({ hit, attribute: snippetAttribute })
		: getScalar(hit.excerpt);
	const imageUrl = getSafeUrl(hit.image?.url);
	const imageAlt = getScalar(hit.image?.alt);
	const imageSrcset = getSafeSrcset(hit.image?.srcset);
	const tags = getTagValues(hit.tags);
	const meta = getMetaValues(hit.meta_footer);
	const external = hit.external === true;

	return html`
		<article class="search-result-card post-card post-card--search-result card :version-2 :align-left"
			data-pattern="search-result-card">
			<div class="search-result-card__inner post-card__inner post-card--search-result__inner pattern__inner :g">
				${permalink ? html`
					<a class="overlink" data-pattern="overlink" href="${permalink}">
						<span class="screen-reader-only">${fallbackTitle}</span>
					</a>
				` : ''}
				${imageUrl ? html`
					<div class="search-result-card__image post-card__image post-card--search-result__image">
						<figure class="image media-figure :g-x :ratio-normal" data-pattern="image">
							<div class="image__inner pattern__inner :g-x">
								<div class="image__media media-figure__media">
									${imageSrcset ? html`
										<img class="media-figure__source" src="${imageUrl}" alt="${imageAlt}"
											loading="lazy" srcset="${imageSrcset}" />
									` : html`
										<img class="media-figure__source" src="${imageUrl}" alt="${imageAlt}"
											loading="lazy" />
									`}
								</div>
							</div>
						</figure>
					</div>
				` : ''}
				<div class="search-result-card__text post-card__text post-card--search-result__text">
					${tags.length ? html`
						<div class="post-card__header post-card--search-result__header">
							<menu class="tag-group" data-pattern="tag-group">
								${tags.map((tag) => html`
									<li class="tag-group__item">
										${tag.link ? html`
											<a href="${tag.link}" class="tag tag--link${tag.taxonomy
												? ` tag--taxonomy-${tag.taxonomy}`
												: ''}" data-pattern="tag">
												<span class="tag__text">${tag.text}</span>
											</a>
										` : html`
											<span class="tag tag--static${tag.taxonomy
												? ` tag--taxonomy-${tag.taxonomy}`
												: ''}" data-pattern="tag">
												<span class="tag__text">${tag.text}</span>
											</span>
										`}
									</li>
								`)}
							</menu>
						</div>
					` : ''}
					${hasTitle ? html`
						<h3 class="search-result-card__title post-card__title-external post-card--search-result__title-external title-external title-external--${external ? 'external' : 'internal'} title">
							${permalink
								? html`<a class="post-card__title-external__link shy" href="${permalink}">${title}</a>`
								: html`<span class="post-card__title-external__text shy">${title}</span>`}
							${external ? html`<span class="external-link" aria-label="External link">↗</span>` : ''}
						</h3>
					` : ''}
					${abstract ? html`
						<div class="search-result-card__abstract post-card__abstract post-card--search-result__abstract card__abstract" data-text-content>
							${abstract}
						</div>
					` : ''}
					${meta.length ? html`
						<footer class="post-card__footer post-card--search-result__footer">
							<div class="post-card__meta post-card--search-result__meta">
								<div class="meta-list" data-pattern="meta-list">
								${meta.map((item) => html`
									<p class="meta-list__item meta-list__item--${item.key}">${item.text}</p>
								`)}
								</div>
							</div>
						</footer>
					` : ''}
				</div>
			</div>
		</article>
	`;
};

const getControlledFacets = (facets) => ({
	searchEnabled: facets.some((facet) => facet.type === 'search'),
	refinementAttributes: facets
		.filter((facet) => facet.type === 'refinement_list' && facet.attribute)
		.map((facet) => facet.attribute),
	dateAttribute: facets.find((facet) => facet.type === 'date' && facet.attribute)?.attribute,
});

const hasControlledState = (indexState, controlled) => {
	if (controlled.searchEnabled && indexState.query) {
		return true;
	}
	if (controlled.refinementAttributes.some(
		(attribute) => indexState.refinementList?.[attribute]?.length
	)) {
		return true;
	}
	if (controlled.dateAttribute && indexState.range?.[controlled.dateAttribute]) {
		return true;
	}
	return Number(indexState.page) > 1;
};

const clearControlledState = (uiState, indexName, controlled) => {
	const indexState = { ...(uiState[indexName] || {}) };

	if (controlled.searchEnabled) {
		delete indexState.query;
	}
	if (indexState.refinementList) {
		indexState.refinementList = { ...indexState.refinementList };
		controlled.refinementAttributes.forEach(
			(attribute) => delete indexState.refinementList[attribute]
		);
		if (!Object.keys(indexState.refinementList).length) {
			delete indexState.refinementList;
		}
	}
	if (controlled.dateAttribute && indexState.range) {
		indexState.range = { ...indexState.range };
		delete indexState.range[controlled.dateAttribute];
		if (!Object.keys(indexState.range).length) {
			delete indexState.range;
		}
	}
	delete indexState.page;

	return {
		...uiState,
		[indexName]: indexState,
	};
};

const createClearAllWidget = ({ containers, indexName, facets, onClear }) => {
	const controlled = getControlledFacets(facets);
	let buttons = [];

	const update = (instantSearchInstance) => {
		const indexState = instantSearchInstance.getUiState()[indexName] || {};
		const canClear = hasControlledState(indexState, controlled);
		buttons.forEach((button) => {
			button.disabled = !canClear;
			button.hidden = !canClear;
		});
	};

	return {
		init({ instantSearchInstance }) {
			buttons = containers.map((container) => {
				const button = document.createElement('button');
				button.type = 'button';
				button.className = 'algolia-filter__clear-all';
				button.textContent = 'Clear All';
				button.setAttribute('aria-label', 'Clear all search filters');
				button.addEventListener('click', () => {
					onClear();
					instantSearchInstance.setUiState(
						(uiState) => clearControlledState(uiState, indexName, controlled)
					);
				});
				container.replaceChildren(button);
				return button;
			});
			update(instantSearchInstance);
		},
		render({ instantSearchInstance }) {
			update(instantSearchInstance);
		},
		dispose() {
			containers.forEach((container) => container.replaceChildren());
			buttons = [];
		},
	};
};

const initializeDrawer = (root) => {
	const drawer = root.querySelector('[data-algolia-drawer]');
	const drawerContent = root.querySelector('[data-algolia-drawer-content]');
	const openButton = root.querySelector('[data-algolia-drawer-open]');
	const closeButtons = [...root.querySelectorAll('[data-algolia-drawer-close]')];
	const closeButton = root.querySelector('.algolia-filter__drawer-close');
	if (!drawer || !drawerContent || !openButton || !closeButton || !closeButtons.length) {
		return;
	}

	const mobileQuery = window.matchMedia('(max-width: 899.98px)');
	let returnFocus = null;
	const close = ({ restoreFocus = true } = {}) => {
		drawer.dataset.isActive = 'false';
		if (mobileQuery.matches) {
			drawer.setAttribute('aria-hidden', 'true');
			drawer.setAttribute('inert', '');
		} else {
			drawer.setAttribute('aria-hidden', 'false');
			drawer.removeAttribute('inert');
		}
		openButton.setAttribute('aria-expanded', 'false');
		document.body.classList.remove('algolia-filter-drawer-open');
		if (restoreFocus && returnFocus instanceof HTMLElement) {
			returnFocus.focus();
		}
	};
	const open = () => {
		if (!mobileQuery.matches) {
			return;
		}
		returnFocus = document.activeElement;
		drawer.dataset.isActive = 'true';
		drawer.setAttribute('aria-hidden', 'false');
		drawer.removeAttribute('inert');
		drawerContent.setAttribute('role', 'dialog');
		drawerContent.setAttribute('aria-modal', 'true');
		openButton.setAttribute('aria-expanded', 'true');
		document.body.classList.add('algolia-filter-drawer-open');
		closeButton.focus();
	};

	const syncMode = () => {
		if (mobileQuery.matches) {
			drawerContent.setAttribute('role', 'dialog');
			drawerContent.setAttribute('aria-modal', 'true');
		} else {
			drawerContent.removeAttribute('role');
			drawerContent.removeAttribute('aria-modal');
		}
		close({ restoreFocus: false });
	};

	syncMode();
	openButton.addEventListener('click', open);
	closeButtons.forEach((button) => button.addEventListener('click', () => close()));
	document.addEventListener('keydown', (event) => {
		if (drawer.dataset.isActive !== 'true') {
			return;
		}
		if (event.key === 'Escape') {
			close();
			return;
		}
		if (event.key === 'Tab') {
			const focusable = [...drawer.querySelectorAll(
				'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), '
				+ 'textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
			)].filter((element) => element.getClientRects().length);
			if (!focusable.length) {
				event.preventDefault();
				return;
			}
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		}
	});

	mobileQuery.addEventListener('change', syncMode);
};

export const transformCurrentRefinements = (items, facets, timeZone) => {
	const descriptors = new Map();
	facets.forEach((facet) => {
		if (facet.type === 'search') {
			descriptors.set('query', facet);
		} else if (facet.attribute) {
			descriptors.set(facet.attribute, facet);
		}
	});

	return items.map((item) => {
		const descriptor = descriptors.get(item.attribute);
		if (!descriptor) {
			return item;
		}

		return {
			...item,
			label: descriptor.label,
			refinements: descriptor.type === 'date'
				? item.refinements.map((refinement) => ({
					...refinement,
					label: getDateRefinementLabel(refinement, timeZone),
				}))
				: item.refinements,
		};
	});
};

const cloneTemplateContent = (root, name) => {
	const template = root.querySelector(`template[data-algolia-template="${name}"]`);
	return template instanceof HTMLTemplateElement
		? template.content.firstElementChild?.cloneNode(true)
		: null;
};

const createRefinementListWidget = ({ connector, root, container, descriptor }) => (
	connector(({ items, refine }) => {
		const fragment = document.createDocumentFragment();

		items.forEach((item) => {
			const option = cloneTemplateContent(root, 'refinement-option');
			const input = option?.querySelector('input[type="checkbox"]');
			const label = option?.querySelector('.toggle-field-option__label');
			if (!option || !input || !label) {
				return;
			}

			input.name = descriptor.attribute;
			input.value = item.value;
			input.checked = item.isRefined;
			input.setAttribute('data-filter-id', `${descriptor.id}-${item.value}`);
			label.textContent = `${item.label} (${item.count})`;
			input.addEventListener('change', () => refine(item.value));
			fragment.append(option);
		});

		container.replaceChildren(fragment);
	})({
		attribute: descriptor.attribute,
		limit: maxFacetValues,
		operator: 'or',
		sortBy: ['name:asc'],
	})
);

const createCurrentRefinementsWidget = ({
	connector,
	root,
	container,
	includedAttributes,
	facets,
	timeZone,
}) => connector(({ items, refine }) => {
	const fragment = document.createDocumentFragment();

	transformCurrentRefinements(items, facets, timeZone).forEach((item) => {
		item.refinements.forEach((refinement) => {
			const button = cloneTemplateContent(root, 'applied-filter');
			const text = button?.querySelector('.button--applied-filter__text, .button__text');
			if (!button || !text) {
				return;
			}

			const label = getScalar(refinement.label);
			text.textContent = label;
			button.type = 'button';
			button.setAttribute('aria-label', `Remove ${label}`);
			button.addEventListener('click', () => refine(refinement));
			fragment.append(button);
		});
	});

	container.replaceChildren(fragment);
})({
	includedAttributes,
});

const updateResultCount = (container, count) => {
	const countElement = container.querySelector('.count');
	if (!countElement) {
		return;
	}

	countElement.textContent = String(count);
	let suffix = countElement.nextSibling;
	if (!suffix || suffix.nodeType !== Node.TEXT_NODE) {
		suffix = document.createTextNode('');
		countElement.after(suffix);
	}
	suffix.textContent = ` Result${count === 1 ? '' : 's'}`;
};

const createStatsWidget = ({ connector, containers }) => connector(({ nbHits }) => {
	containers.forEach((container) => updateResultCount(container, nbHits));
})({});

export const addDateWidget = (search, root, descriptor, timeZone, connectors) => {
	const container = getMount(root, `date-${descriptor.id}`);
	const inputs = container ? [...container.querySelectorAll('input[type="radio"]')] : [];
	if (!container || !inputs.length || !connectors.connectRange) {
		return false;
	}

	let refineRange = null;
	const handleChange = (event) => {
		if (!event.target.checked) {
			return;
		}
		const range = datePeriodToRange(event.target.value, timeZone);
		if (range) {
			refineRange(range);
		}
	};

	const dateWidget = connectors.connectRange(({ start, refine }, isFirstRender) => {
		refineRange = refine;
		if (isFirstRender) {
			inputs.forEach((input) => input.addEventListener('change', handleChange));
		}

		const selected = rangeToDatePeriod(start, timeZone);
		inputs.forEach((input) => {
			input.checked = input.value === selected;
		});
	})({
		attribute: descriptor.attribute,
	});

	search.addWidgets([dateWidget]);
	return true;
};

const addFacetWidgets = (search, root, facets, widgets, connectors) => {
	facets.forEach((facet) => {
		if (facet.type === 'search') {
			const container = getSearchMount(root);
			if (container) {
				search.addWidgets([widgets.searchBox({
					container,
					placeholder: facet.label,
					showLoadingIndicator: true,
					queryHook: createSearchQueryHook(),
				})]);
			}
		}

		if (facet.type === 'refinement_list' && facet.attribute) {
			const container = getMount(root, `facet-${facet.id}`);
			if (container && connectors.connectRefinementList) {
				search.addWidgets([createRefinementListWidget({
					connector: connectors.connectRefinementList,
					root,
					container,
					descriptor: facet,
				})]);
			}
		}
	});
};

const initialize = (config) => {
	const root = document.getElementById(config.instance_id);
	if (!root || !root.matches('.algolia-filter[data-algolia-filter]')) {
		return;
	}

	const facets = Array.isArray(config.facets) ? config.facets : [];
	const dateDescriptor = facets.find((facet) => facet.type === 'date' && facet.attribute);
	const requiredMounts = ['stats', 'current-refinements', 'clear-refinements', 'hits', 'status'];
	const hasRequiredMounts = requiredMounts.every((name) => getMount(root, name));
	const hasConfig = config.application_id
		&& config.search_api_key
		&& config.primary_index_name
		&& Array.isArray(config.allowed_post_types)
		&& config.allowed_post_types.length;

	if (
		!hasRequiredMounts
		|| !hasConfig
		|| (dateDescriptor && !isValidTimeZone(config.site_timezone))
		|| typeof window.algoliasearch !== 'function'
		|| typeof window.instantsearch !== 'function'
		|| !window.instantsearch.widgets
		|| typeof window.instantsearch.widgets.infiniteHits !== 'function'
		|| !window.instantsearch.connectors?.connectStats
		|| !window.instantsearch.connectors?.connectCurrentRefinements
		|| (
			facets.some((facet) => facet.type === 'refinement_list' && facet.attribute)
			&& !window.instantsearch.connectors?.connectRefinementList
		)
		|| !window.instantsearch.routers?.history
	) {
		setStatus(root, unavailableMessage, 'error');
		return;
	}

	const widgets = window.instantsearch.widgets;
	const connectors = window.instantsearch.connectors || {};
	const configureSearchParameters = {
		hitsPerPage: config.results_per_page,
		filters: getPostTypeFilter(config.allowed_post_types),
		facetingAfterDistinct: true,
		maxValuesPerFacet: maxFacetValues,
	};
	moveSearchToExternalHost(root);
	initializeDrawer(root);
	const search = window.instantsearch({
		indexName: config.primary_index_name,
		searchClient: window.algoliasearch(config.application_id, config.search_api_key),
		routing: getRouting({
			config,
			facets,
			dateDescriptor,
			historyRouter: window.instantsearch.routers.history,
			onDateError: (message) => setDateError(root, message),
		}),
	});
	const includedRefinements = facets.flatMap((facet) => {
		if (facet.type === 'search') {
			return ['query'];
		}
		return facet.attribute ? [facet.attribute] : [];
	});

	search.addWidgets([
		widgets.configure(configureSearchParameters),
		createStatsWidget({
			connector: connectors.connectStats,
			containers: [...root.querySelectorAll('[data-algolia-result-count]')],
		}),
		createCurrentRefinementsWidget({
			connector: connectors.connectCurrentRefinements,
			root,
			container: getMount(root, 'current-refinements'),
			includedAttributes: includedRefinements,
			facets,
			timeZone: config.site_timezone,
		}),
		createClearAllWidget({
			containers: getMounts(root, 'clear-refinements'),
			indexName: config.primary_index_name,
			facets,
			onClear: () => setDateError(root, ''),
		}),
		widgets.infiniteHits({
			container: getMount(root, 'hits'),
			cssClasses: {
				loadMore: 'button button--standard algolia-filter__load-more-button :version-2 :size-1',
				disabledLoadMore: 'algolia-filter__load-more-button--disabled',
			},
			templates: {
				empty(results, { html }) {
					return html`<p class="algolia-filter__empty" aria-hidden="true">No results found.</p>`;
				},
				item: getHitTemplate,
				showMoreText: 'Load More',
			},
		}),
	]);

	addFacetWidgets(search, root, facets, widgets, connectors);
	if (
		dateDescriptor
		&& !addDateWidget(search, root, dateDescriptor, config.site_timezone, connectors)
	) {
		setStatus(root, unavailableMessage, 'error');
		return;
	}

	search.on('render', () => {
		associateSearchInput(root, config.instance_id, search.helper?.state?.query || '');
		const results = search.helper && search.helper.lastResults;
		if (results) {
			setStatus(
				root,
				results.nbHits === 1 ? '1 result.' : `${results.nbHits} results.`,
				'announcement'
			);
		}
	});
	search.on('error', () => setStatus(root, unavailableMessage, 'error'));

	try {
		search.start();
	} catch (error) {
		setStatus(root, unavailableMessage, 'error');
	}
};

export const initializeWhenVisible = (config, initializeComponent = initialize) => {
	const root = document.getElementById(config.instance_id);
	if (
		!root
		|| !root.matches('.algolia-filter[data-algolia-filter]')
		|| scheduledRoots.has(root)
	) {
		return;
	}

	scheduledRoots.add(root);
	const run = () => {
		if (initializedRoots.has(root)) {
			return;
		}
		initializedRoots.add(root);
		initializeComponent(config);
	};

	if (
		getExternalSearchHost(root)
		|| typeof window.IntersectionObserver !== 'function'
	) {
		run();
		return;
	}

	const observer = new window.IntersectionObserver((entries) => {
		if (!entries.some((entry) => entry.isIntersecting)) {
			return;
		}

		observer.disconnect();
		run();
	});
	observer.observe(root);
};

if (Array.isArray(queue)) {
	queue.forEach((config) => initializeWhenVisible(config));
}
